import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback, Fragment } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { Message } from '@/types';
import MessageItem from './MessageItem';
import type { SelectionScope } from '@/store/selectionStore';
import { useBlockedUsersStore } from '@/store/blockedUsersStore';

interface MessageListProps {
  messages: Message[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  onReply?: (message: Message) => void;
  /**
   * Phase 8.1 — id первого непрочитанного сообщения. Если задан и
   * присутствует среди отрисованных, перед ним показывается красная
   * полоса «непрочитанные».
   */
  firstUnreadId?: string | null;
  /** Phase 11.3 — pass-through to MessageItem so multi-select knows scope. */
  selectionScope?: SelectionScope;
  /** Phase 20 — per-channel IRC classic rendering. */
  chatStyle?: 'BUBBLE' | 'CLASSIC';
  /** Phase 12.3 — embed iframe (russnt.ru) — usted limits context menu. */
  embedMode?: boolean;
}

function formatDay(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (msgDay.getTime() === today.getTime()) return 'Сегодня';
  if (msgDay.getTime() === yesterday.getTime()) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function isDayBreak(curr: Message, prev?: Message): boolean {
  if (!prev) return true;
  const d1 = new Date(curr.createdAt);
  const d2 = new Date(prev.createdAt);
  return d1.toDateString() !== d2.toDateString();
}

export default function MessageList({
  messages: allMessages,
  onLoadMore,
  hasMore,
  loading,
  onReply,
  firstUnreadId,
  selectionScope,
  chatStyle,
  embedMode,
}: MessageListProps) {
  const blockedIds = useBlockedUsersStore((s) => s.ids);
  const messages = useMemo(
    () => (blockedIds.length ? allMessages.filter((m) => !blockedIds.includes(m.authorId)) : allMessages),
    [allMessages, blockedIds],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Phase 8.1 — scroll-state
  const [atBottom, setAtBottom] = useState(true);
  const [unseenCount, setUnseenCount] = useState(0);
  const [stickyDay, setStickyDay] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  // Track previous length to detect new arrivals
  const prevLenRef = useRef(messages.length);

  // Detect at-bottom state on scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      const nowAtBottom = distance < 80;
      setAtBottom(nowAtBottom);
      if (nowAtBottom) setUnseenCount(0);

      // Infinite scroll up
      if (el.scrollTop < 80 && hasMore && !loading) onLoadMore?.();

      // Sticky day header — найти первое видимое сообщение и взять его дату
      const items = el.querySelectorAll<HTMLElement>('[data-message-id]');
      const containerTop = el.getBoundingClientRect().top;
      let topMsgDate: string | null = null;
      for (let i = 0; i < items.length; i++) {
        const rect = items[i].getBoundingClientRect();
        if (rect.bottom > containerTop + 8) {
          topMsgDate = items[i].dataset.createdat ?? null;
          break;
        }
      }
      if (topMsgDate) setStickyDay(formatDay(topMsgDate));
      else setStickyDay(null);
    };
    el.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => el.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, onLoadMore, messages.length]);

  // Initial scroll: on first message load, scroll to unread separator or bottom.
  //
  // Hard reload (Ctrl+Shift+R) bypasses the image cache, so <img> tags are
  // still 0-height when the first layout runs. If we only scroll once, images
  // load later and leave us stranded in the middle. We keep re-pinning to the
  // target while images/DOM settle — capped at 2.5 s or until the user scrolls.
  const didInitialScroll = useRef(false);
  const stillPinning = useRef(true);
  const pinTarget = useRef<{ kind: 'unread'; id: string } | { kind: 'bottom' } | null>(null);

  useLayoutEffect(() => {
    if (didInitialScroll.current || messages.length === 0) return;
    const el = containerRef.current;
    if (!el) return;

    pinTarget.current = firstUnreadId
      ? { kind: 'unread', id: firstUnreadId }
      : { kind: 'bottom' };

    const scrollToTarget = () => {
      const c = containerRef.current;
      if (!c || !pinTarget.current) return;
      if (pinTarget.current.kind === 'unread') {
        const unreadEl = c.querySelector<HTMLElement>(`[data-message-id="${pinTarget.current.id}"]`);
        if (unreadEl) {
          unreadEl.scrollIntoView({ block: 'center', behavior: 'auto' });
          return;
        }
        // unread separator not in DOM (yet) — fall through to bottom
      }
      c.scrollTop = c.scrollHeight;
    };

    // First anchoring attempt — after layout is committed
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToTarget();
        didInitialScroll.current = true;
      });
    });

    // Re-anchor whenever list height grows (late image loads, fonts, etc.)
    const ro = new ResizeObserver(() => {
      if (stillPinning.current) scrollToTarget();
    });
    ro.observe(el);

    // Also re-anchor on each image load — ResizeObserver + img load are both
    // needed because RO on the scroll container doesn't always fire for
    // inner image size changes when the container itself stays the same.
    const onImgLoad = () => { if (stillPinning.current) scrollToTarget(); };
    const attachImgListeners = () => {
      el.querySelectorAll('img').forEach((img) => {
        if (!(img as any).__pinWired) {
          img.addEventListener('load', onImgLoad, { once: true });
          img.addEventListener('error', onImgLoad, { once: true });
          (img as any).__pinWired = true;
        }
      });
    };
    attachImgListeners();
    // New images may be inserted by MessageItem after initial render —
    // re-scan a few times during the pin window.
    const rescanTimers = [150, 400, 800, 1500].map((ms) =>
      window.setTimeout(attachImgListeners, ms),
    );

    const stopTimer = window.setTimeout(() => {
      stillPinning.current = false;
    }, 2500);

    return () => {
      ro.disconnect();
      rescanTimers.forEach(window.clearTimeout);
      window.clearTimeout(stopTimer);
    };
  }, [messages.length, firstUnreadId]);

  // Any real user interaction stops auto-pinning immediately.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const stop = () => { stillPinning.current = false; };
    el.addEventListener('wheel', stop, { passive: true });
    el.addEventListener('touchstart', stop, { passive: true });
    el.addEventListener('keydown', stop);
    return () => {
      el.removeEventListener('wheel', stop);
      el.removeEventListener('touchstart', stop);
      el.removeEventListener('keydown', stop);
    };
  }, []);

  // Reset initial scroll flag when channel changes (messages become empty then refill)
  useEffect(() => {
    if (messages.length === 0) didInitialScroll.current = false;
  }, [messages.length]);

  // Smart auto-scroll: only stick to bottom if user was already there.
  // Otherwise bump unread counter.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const grew = messages.length > prevLenRef.current;
    prevLenRef.current = messages.length;
    if (!grew) return;
    if (atBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setUnseenCount((c) => c + 1);
    }
  }, [messages.length, atBottom]);

  // Phase 8.1 — jump to a message by id (used by replies / search)
  const jumpTo = useCallback((id: string) => {
    const el = containerRef.current?.querySelector<HTMLElement>(`[data-message-id="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(id);
    window.setTimeout(() => setHighlightId((cur) => (cur === id ? null : cur)), 2000);
  }, []);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setUnseenCount(0);
  };

  // Build a render list interleaving day separators and unread separator
  const renderItems = useMemo(() => {
    const out: Array<
      | { kind: 'day'; key: string; label: string }
      | { kind: 'unread'; key: string }
      | { kind: 'msg'; key: string; msg: Message; prev?: Message }
    > = [];
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const prev = messages[i - 1];
      if (isDayBreak(msg, prev)) {
        out.push({ kind: 'day', key: `d-${msg.id}`, label: formatDay(msg.createdAt) });
      }
      if (firstUnreadId && msg.id === firstUnreadId) {
        out.push({ kind: 'unread', key: `u-${msg.id}` });
      }
      out.push({ kind: 'msg', key: msg.id, msg, prev });
    }
    return out;
  }, [messages, firstUnreadId]);

  return (
    <div className="relative flex-1 flex flex-col min-h-0">
      {/* min-h-0 ОБЯЗАТЕЛЕН: flex-item по умолчанию `min-height: auto`
          (= размер контента), что блокирует срабатывание overflow-y-auto.
          Без этого список «вытекает» за границы flex-родителя вместо того,
          чтобы скроллиться — главная причина «не работает скролл в чате»
          в embed iframe (где flex chain особо чувствителен к этому).

          touchAction + overscrollBehavior: гарантия что мобильный браузер
          доставляет вертикальный scroll именно сюда, а не propagate'ит в parent
          (важно для embed iframe — родительская страница русnt не должна
          перехватывать touchscroll). */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col chat-bg messages-scroll"
        style={{ touchAction: 'pan-y', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
      >
        {hasMore && (
          <div className="flex justify-center py-3">
            {loading ? (
              <div className="flex gap-1 items-center text-surface-500 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-surface-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-surface-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-surface-500 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            ) : (
              <button
                onClick={onLoadMore}
                className="text-xs text-surface-500 hover:text-surface-300 px-4 py-1.5 rounded-full bg-surface-800/50 transition-colors"
              >
                Загрузить старые сообщения
              </button>
            )}
          </div>
        )}

        {messages.length === 0 && !loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-surface-500 text-sm">Сообщений пока нет</p>
              <p className="text-surface-600 text-xs mt-1">Будьте первым!</p>
            </div>
          </div>
        )}

        <div className="flex flex-col pb-3">
          {renderItems.map((it) => {
            if (it.kind === 'day') {
              return (
                <div key={it.key} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 h-px bg-surface-800" />
                  <span className="text-surface-500 text-xs font-medium bg-surface-950 px-2">{it.label}</span>
                  <div className="flex-1 h-px bg-surface-800" />
                </div>
              );
            }
            if (it.kind === 'unread') {
              return (
                <div key={it.key} className="flex items-center gap-3 px-4 py-1.5">
                  <div className="flex-1 h-px bg-red-500/60" />
                  <span className="text-red-400 text-[10px] font-semibold uppercase tracking-wider">Непрочитанные</span>
                  <div className="flex-1 h-px bg-red-500/60" />
                </div>
              );
            }
            return (
              <Fragment key={it.key}>
                <DateAttrWrapper createdAt={it.msg.createdAt} id={it.msg.id}>
                  <MessageItem
                    message={it.msg}
                    previousMessage={it.prev}
                    onReply={onReply}
                    highlight={highlightId === it.msg.id}
                    onJumpTo={jumpTo}
                    selectionScope={selectionScope}
                    chatStyle={chatStyle}
                    embedMode={embedMode}
                  />
                </DateAttrWrapper>
              </Fragment>
            );
          })}
        </div>

        <div ref={bottomRef} />
      </div>

      {/* Sticky day header */}
      {stickyDay && messages.length > 0 && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
          <span className="text-surface-300 text-xs font-medium bg-surface-900/80 backdrop-blur-sm border border-surface-700 px-3 py-1 rounded-full shadow">
            {stickyDay}
          </span>
        </div>
      )}

      {/* Scroll-to-bottom floating button */}
      {!atBottom && (
        <button
          type="button"
          onClick={scrollToBottom}
          className={clsx(
            'absolute right-4 bottom-4 z-10 w-10 h-10 rounded-full bg-surface-800 border border-surface-700 text-surface-200 shadow-xl',
            'flex items-center justify-center hover:bg-surface-700 transition-colors',
          )}
        >
          <ChevronDown size={18} />
          {unseenCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[10px] font-semibold flex items-center justify-center">
              {unseenCount > 99 ? '99+' : unseenCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Tiny wrapper that propagates `data-createdat` onto MessageItem's outer
 * div without forcing MessageItem itself to expose a ref. We attach the
 * attribute by walking the rendered DOM after mount via a ref callback.
 */
function DateAttrWrapper({ id, createdAt, children }: { id: string; createdAt: string; children: React.ReactNode }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = wrapRef.current?.querySelector<HTMLElement>(`[data-message-id="${id}"]`);
    if (el) el.dataset.createdat = createdAt;
  }, [id, createdAt]);
  return <div ref={wrapRef} className="contents">{children}</div>;
}
